import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { unsubscribeApply, unsubscribeInfo } from '@/lib/api';

const UnsubscribePage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, email: null, already: false, done: false, error: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info = await unsubscribeInfo(token);
        if (!mounted) return;
        setState((s) => ({ ...s, loading: false, email: info.email, already: !!info.already_unsubscribed }));
      } catch (err) {
        if (!mounted) return;
        setState({ loading: false, email: null, already: false, done: false, error: err?.response?.data?.detail || 'This unsubscribe link is invalid.' });
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const confirm = async () => {
    try {
      await unsubscribeApply(token);
      setState((s) => ({ ...s, done: true, already: true }));
      toast.success('Unsubscribed. You will no longer receive marketing emails.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unsubscribe. Please try again.');
    }
  };

  return (
    <div className="container-pd py-16 max-w-md" data-testid="unsubscribe-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Email preferences</div>
      <h1 className="font-display text-5xl leading-none mt-3">Unsubscribe.</h1>

      {state.loading ? (
        <div className="mt-6 text-sm text-muted-sage" data-testid="unsubscribe-loading">Checking your link…</div>
      ) : state.error ? (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 p-5 text-sm" data-testid="unsubscribe-error">
          <div className="text-[color:var(--pd-text)] font-medium">Invalid link.</div>
          <p className="text-muted-sage mt-1">{state.error}</p>
        </div>
      ) : state.done || state.already ? (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-gold)]/40 bg-surface p-6" data-testid="unsubscribe-done">
          <div className="font-display text-2xl">You&rsquo;re unsubscribed.</div>
          <p className="text-sm text-muted-sage mt-2">
            <span className="text-[color:var(--pd-text)]">{state.email}</span> will no longer receive marketing emails from The Padel Draw. You may still receive transactional emails (e.g. entry confirmations) as they&rsquo;re required for your account.
          </p>
          <p className="text-xs text-muted-sage mt-3">
            Change your mind later? You can re-enable marketing emails from{' '}
            <Link to="/account" className="text-[color:var(--pd-accent-yellow)] underline">your account settings</Link>.
          </p>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-gold)]/40 bg-surface p-6" data-testid="unsubscribe-confirm">
          <div className="font-display text-2xl">Confirm unsubscribe</div>
          <p className="text-sm text-muted-sage mt-2">
            You&rsquo;re about to stop marketing emails to <span className="text-[color:var(--pd-text)]">{state.email}</span>.
          </p>
          <button
            type="button"
            onClick={confirm}
            className="mt-5 w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-3.5 font-medium"
            data-testid="unsubscribe-confirm-button"
          >
            Unsubscribe me
          </button>
          <p className="text-xs text-muted-sage mt-3 text-center">
            <Link to="/" className="underline hover:text-[color:var(--pd-accent-yellow)]">Never mind, keep me subscribed</Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default UnsubscribePage;
